import { useState } from 'react';
import { toast } from 'react-toastify';
import { actions, initialState } from 'state';
import { useDispatch } from 'react-redux';
import { analytics, api, type ApiError, billing, payments, useUser } from 'libs';
import { Button, Loader } from 'components/common';
import { TrialOfferSummary, UpgradeAccountForm } from 'components/subscriptions';
import type { PaymentFormSubmitData, SubscriptionPrice } from 'components/subscriptions/types';

interface FreeTrialProps {
  nextStep: () => Promise<void>;
}

const FreeTrial = ({ nextStep }: FreeTrialProps) => {
  const dispatch = useDispatch<any>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const user = useUser();
  const hasExistingBrokerage = user.features.has_brokerage;
  const hasExistingJoinRequest = Boolean(user.join_request);
  const isAddonOnlyTrial = user.features.is_pro;
  const platinumAccessSource = hasExistingBrokerage ? 'team' : 'Finmo connection';
  const trialAnalyticsPlan = isAddonOnlyTrial ? 'Free trial - add-ons' : 'Free trial - all subscriptions';
  const trialButtonLabel = isAddonOnlyTrial ? 'Start my 30-day add-on free trial' : 'Start my 30-day free trial';
  const trialSuccessMessage = isAddonOnlyTrial
    ? 'Your AI Assistant and Scenarios free trial has started.'
    : 'Your free trial has started.';
  const trialSuccessToastId = isAddonOnlyTrial ? 'toast-success-addon-free-trial' : 'toast-success-free-trial';
  const trialPrices = billing.trialPrices(initialState.prices, isAddonOnlyTrial) as SubscriptionPrice[];
  const trialOffer = billing.trialOffer(trialPrices);
  const trialNote = (() => {
    if (isAddonOnlyTrial) {
      return (
        <span>
          Your Platinum access is already included through your {platinumAccessSource}. This trial adds AI Assistant and
          Scenarios.
        </span>
      );
    }

    if (hasExistingJoinRequest) {
      return (
        <span>
          Once your team admin accepts your request to join the team, you will have Platinum access. However, AI
          Assistant and Scenarios are not included and require separate subscriptions.
        </span>
      );
    }

    return null;
  })();

  const startFreeTrial = async (data: PaymentFormSubmitData) => {
    setIsSubmitting(true);
    const paymentData = {
      ...(data.setupIntent ? payments.parseStripeToken(data.setupIntent) : null),
      pm_postal: data.pm_postal,
    };

    try {
      await api.post('subscription/trial', paymentData);

      analytics.billing.subscribe(trialAnalyticsPlan);

      toast.success(trialSuccessMessage, {
        toastId: trialSuccessToastId,
      });

      await dispatch(actions.users.fetchMe());
      await nextStep();
    } catch (error) {
      toast.error(
        (error as ApiError).response?.data.message ||
          "We couldn't process your request. Please contact customer support if this problem persists."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="p-5" data-testid="onboarding-free-trial-step">
        {showPaymentForm ? (
          <UpgradeAccountForm
            confirmation={false}
            description={null}
            footer={
              <span>
                You can downgrade your subscriptions at anytime during your trial to avoid being automatically charged
                at the end.
              </span>
            }
            header={<h3 className="fw-bold">Start your 30-day {isAddonOnlyTrial ? 'add-on ' : ''}free trial</h3>}
            isSubmitting={isSubmitting}
            onSubmit={startFreeTrial}
            paymentButtonLabel={trialButtonLabel}
            secondaryAction={
              <div className="d-flex gap-2">
                <Button
                  onClick={() => setShowPaymentForm(false)}
                  testId="onboarding-free-trial-back-button"
                  text="bold"
                  variant="border"
                >
                  Back
                </Button>
              </div>
            }
            trial={{ note: trialNote, prices: trialPrices }}
            user={user}
            variant="inline"
          />
        ) : (
          <>
            <h3 className="fw-bold text-center">Start your 30-day {isAddonOnlyTrial ? 'add-on ' : ''}free trial</h3>
            <TrialOfferSummary className="my-3" note={trialNote} {...trialOffer} />
            <div className="d-flex gap-2 flex-column">
              <Button
                onClick={() => setShowPaymentForm(true)}
                testId="onboarding-free-trial-start-button"
                text="bold"
                variant="primary-dark"
              >
                <div className="p-3">Yes, I want to start a free trial</div>
              </Button>
              <Button onClick={nextStep} testId="onboarding-free-trial-skip-button" text="bold" variant="border">
                <div className="p-3">No, continue onboarding</div>
              </Button>
            </div>
          </>
        )}
      </div>
      <Loader isLoading={isSubmitting} message="Starting your free trial..." />
    </>
  );
};

export default FreeTrial;
